import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Package, Tag, DollarSign, TrendingUp, Printer } from 'lucide-react'
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

const CORES = ['#0069c4', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#ef4444', '#64748b']

export default function ProdutosPorCategoriaPage() {
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery<any>({
    queryKey: ['relatorio-prod-cat'],
    queryFn: async () => (await api.get('/relatorios/produtos-por-categoria')).data,
  })

  if (isLoading) return <div className="text-ink-500">Carregando...</div>
  if (!data) return null

  function toggle(k: string) {
    const s = new Set(expandidos)
    if (s.has(k)) s.delete(k); else s.add(k)
    setExpandidos(s)
  }

  const pieData = data.grupos.map((g: any) => ({
    name: g.categoria,
    value: g.qtd_produtos,
  }))

  const topCategorias = [...data.grupos]
    .filter((g: any) => g.categoria !== 'Sem Categoria')
    .sort((a: any, b: any) => b.valor_estoque_venda - a.valor_estoque_venda)
    .slice(0, 5)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-ink-900">Produtos por Categoria</h2>
          <p className="text-sm text-ink-500 mt-0.5">Visão do catálogo agrupado por categoria com totais de estoque e venda.</p>
        </div>
        <button onClick={() => window.print()} className="btn-secondary">
          <Printer size={14} /> Imprimir
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Categorias" value={String(data.total_categorias)} icon={Tag} tone="brand" />
        <KPI label="Produtos Ativos" value={String(data.total_produtos)} icon={Package} tone="green" />
        <KPI label="Valor Estoque (Custo)" value={formatMoney(data.valor_estoque_custo)} icon={DollarSign} tone="amber" />
        <KPI label="Valor Estoque (Venda)" value={formatMoney(data.valor_estoque_venda)} icon={TrendingUp} tone="green" />
      </div>

      {/* Pie + Top 5 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card card-body lg:col-span-2">
          <h3 className="font-semibold text-ink-900 mb-3">Distribuição de Produtos</h3>
          {pieData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(e: any) => `${e.name} (${e.value})`}
                  >
                    {pieData.map((_: any, i: number) => (
                      <Cell key={i} fill={CORES[i % CORES.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-ink-500 text-center py-12">Sem produtos cadastrados.</div>
          )}
        </div>
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">Top Categorias (por valor venda)</h3>
          {topCategorias.length === 0 ? (
            <div className="text-sm text-ink-500 text-center py-8">Sem dados.</div>
          ) : (
            <ol className="space-y-2">
              {topCategorias.map((c: any, i: number) => (
                <li key={c.categoria} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="font-medium">{c.categoria}</span>
                  </div>
                  <span className="text-ink-600">{formatMoney(c.valor_estoque_venda)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Accordion por categoria */}
      <div className="space-y-2">
        {data.grupos.map((g: any) => {
          const open = expandidos.has(g.categoria)
          return (
            <div key={g.categoria} className="card">
              <button
                onClick={() => toggle(g.categoria)}
                className="w-full px-5 py-3 flex items-center justify-between hover:bg-ink-50 text-left"
              >
                <div className="flex items-center gap-3">
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="font-semibold">{g.categoria}</span>
                  <span className="text-xs text-ink-500">({g.qtd_produtos} {g.qtd_produtos === 1 ? 'produto' : 'produtos'})</span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <span className="text-ink-500">Estoque custo: <span className="font-medium text-ink-800">{formatMoney(g.valor_estoque_custo)}</span></span>
                  <span className="text-ink-500">Valor venda: <span className="font-medium text-emerald-700">{formatMoney(g.valor_estoque_venda)}</span></span>
                </div>
              </button>
              {open && (
                <div className="table-wrap !rounded-t-none !border-t">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descrição</th>
                        <th>Unid</th>
                        <th className="text-right">Estoque</th>
                        <th className="text-right">Custo</th>
                        <th className="text-right">Venda</th>
                        <th className="text-right">Margem</th>
                        <th className="text-right">Valor Estoque</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.produtos.map((p: any) => (
                        <tr key={p.id}>
                          <td className="font-mono text-xs">{p.codigo}</td>
                          <td className="font-medium">{p.descricao}</td>
                          <td className="text-ink-600">{p.unidade}</td>
                          <td className="text-right">{Number(p.estoque_atual).toLocaleString('pt-BR')}</td>
                          <td className="text-right text-ink-600">{formatMoney(p.preco_custo)}</td>
                          <td className="text-right">{formatMoney(p.preco_venda)}</td>
                          <td className="text-right">
                            {p.margem_pct !== null ? (
                              <span className={p.margem_pct >= 30 ? 'text-emerald-600 font-medium' : p.margem_pct >= 0 ? 'text-amber-600' : 'text-red-600 font-medium'}>
                                {p.margem_pct.toFixed(1)}%
                              </span>
                            ) : '-'}
                          </td>
                          <td className="text-right font-medium">{formatMoney(p.valor_estoque_venda)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function KPI({ label, value, icon: Icon, tone }: { label: string; value: string; icon: any; tone: 'brand' | 'green' | 'amber' | 'red' }) {
  const t: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  }
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-ink-500 uppercase tracking-wider">{label}</div>
          <div className="text-xl font-bold text-ink-900 mt-1">{value}</div>
        </div>
        <div className={`p-2 rounded-lg ${t[tone]}`}><Icon size={16} /></div>
      </div>
    </div>
  )
}
